import * as angular from 'angular';
import * as _ from 'lodash';

import {
    ComponentDefinition,
    Identifier,
    Group,
    KeyValuePair,
    AlarmLevel,
    SelectorType,
    ISortConfiguration,
    IFrequency,
    IFrequencyValidationRule,
    IValueViewModel,
    Dictionary
} from '../../models/';
import { IValueTypeHelperService, ILoggerService, IStoreService, toDuration, ICurrentApplicationService } from '../../services/';
import { Store, StoreElement } from '../../stateManagement';
import { IAlarmHelper, AlarmModes } from '../../common/';
import { Alarm, AlarmList } from '../../models/alarms/Alarm';

class IdentifierAlarmsController implements angular.IComponentController {
    alarm: Alarm;
    alarmCategories: string[] = [];
    alarmIndex?: number;
    alarms: Alarm[] = [];
    delayRules: IFrequencyValidationRule[] = [{ key: 'interval', isValid: (f) => this.validateDelay(f, this) }];
    form: angular.IFormController;
    group: Group;
    groupAlarms: AlarmList = {};
    identifier: Identifier;
    isLoaded: boolean;
    isLoading: boolean;
    isEditMode: boolean;
    levelKeys = Object.keys(AlarmLevel);
    levels = AlarmLevel;
    loadingMessage: string;
    modes: KeyValuePair<number, string>[];
    modeList: string[];
    originalAlarms: Alarm[];
    serieType = SelectorType.identifier;
    sortConfig: ISortConfiguration;
    states: any[];
    statesForGroup: any[];
    valueTypes: KeyValuePair<string, string>[];
    viewModels: Dictionary<IValueViewModel>;

    readonly formState: { isManual: boolean; level: AlarmLevel; value: string }[] = [];
    readonly onDelete: (value: { index: number }) => void;
    readonly updateFormState: (value: { index: number; newValues: { isManual?: boolean; level?: AlarmLevel; value?: string } }) => void;

    private _garbages: (() => void)[] = [];
    private _storeLoaded: angular.IDeferred<void>;
    private _baseFreq: IFrequency;

    static $inject = ['$q', '$scope', 'storeService', 'valueTypeHelperService', 'loggerService', 'alarmHelper', 'currentApplicationService'];

    constructor(
        $q: angular.IQService,
        private _$scope: angular.IScope,
        private _storeService: IStoreService,
        private _valueTypeHelper: IValueTypeHelperService,
        private _loggerService: ILoggerService,
        private _alarmHelper: IAlarmHelper,
        currentApplicationService: ICurrentApplicationService
    ) {
        this.sortConfig = {
            animation: 150,
            onSort: (evt) => this.sort(evt)
        };
        this._storeLoaded = $q.defer<void>();
        const conf = currentApplicationService.getApplicationConfiguration();
        this._baseFreq = { interval: conf.interval, intervalTimeUnit: conf.intervalTimeUnit };
    }

    $onInit() {
        // odd trick to get only the first half of keys which are the numerical ones
        this.levelKeys = this.levelKeys.slice(0, this.levelKeys.length / 2);

        this.modes = AlarmModes;

        this._garbages.push(
            this._$scope.$watch(
                () => this.group,
                () => this.load()
            )
        );

        this._garbages.push(
            this._storeService.register((store) => this.refreshStore(store), [StoreElement.AlarmCategories, StoreElement.States, StoreElement.ValueViewModels])
        );
    }

    $onChanges(changesObj: angular.IOnChangesObject) {
        if (changesObj['identifier']) {
            this.refresh();
        }
    }

    $onDestroy() {
        this._garbages.forEach((w) => w());
    }

    private refreshStore(store: Store) {
        const states: any[] = _.values(store.states.dictionary);
        const alarmCategories = _.values(store.alarmCategories.dictionary);
        this.filterGroupState(states);

        this.states = states;
        this.alarmCategories = alarmCategories;
        this.viewModels = store.valueViewModels.dictionary;
        this.isLoading = false;
        this.isLoaded = true;
        this._storeLoaded.resolve();
    }

    private loadAlarmsForIdentifiers(identifierGuids: string[]): angular.IPromise<AlarmList> {
        return this._storeService.dispatchers().alarms.getAllForIdentifiers(identifierGuids);
    }

    private saveAlarm(identifierGuid: string, targetAlarms: Alarm[]): angular.IPromise<AlarmList> {
        return this._storeService.dispatchers().alarms.updateMany(identifierGuid, targetAlarms);
    }

    private deleteAlarm(identifierGuid: string, alarmGuid: string): angular.IPromise<AlarmList> {
        return this._storeService.dispatchers().alarms.deleteOne(identifierGuid, alarmGuid);
    }

    private sort(evt) {
        evt.forEach((s: Alarm, i) => (s.sortingOrder = i));
        this.alarms = angular.copy(evt.models);
        this.save();
    }

    private load() {
        if (this.group) {
            const guidList = this.group.identifierCollection.map((i) => i.guid);
            this._storeLoaded.promise
                .then(() => this.loadAlarmsForIdentifiers(guidList))
                .then((data) => (this.groupAlarms = data))
                .catch(() => (this.groupAlarms = undefined))
                .finally(() => {
                    this.refresh();
                    // auto select first alarm
                    this.alarm = this.alarms && this.alarms.length > 0 ? this.alarms[0] : undefined;
                });
        }
    }

    getValueType(alarm: Alarm) {
        let first: KeyValuePair<string, string>;
        if (this.isLoaded && alarm) {
            let key = alarm.value;
            // Sanitize for older alarms format
            if (!!key && (key[0] === 'A' || key[0] === 'G')) {
                alarm.value = key = key.toLowerCase();
            }

            first = _.find(this.valueTypes, (vt) => vt.key === key);
        }
        return first ? first.value : '';
    }

    private validateDelay(f: IFrequency, self: this): boolean {
        let isValid = false;
        if (f?.interval >= 0) {
            // must be multiple of group freq
            const baseDuration = toDuration(self.group.frequency?.interval !== 0 ? self.group.frequency : self._baseFreq);
            isValid = toDuration(f).asMilliseconds() % baseDuration.asMilliseconds() === 0;
        }
        return f === undefined || isValid;
    }

    private refresh() {
        this.filterAlarms();
        this.filterGroupState(this.states);
        if (this.identifier && this.viewModels && this.viewModels[this.identifier.guid]) {
            this.valueTypes = this._valueTypeHelper.getViewModelValueTypes(this.viewModels[this.identifier.guid]);
        }
    }

    private filterAlarms() {
        if (this.identifier && this.groupAlarms && this.groupAlarms[this.identifier.guid]) {
            this.originalAlarms = _.sortBy(this.groupAlarms[this.identifier.guid], (a: Alarm) => a.sortingOrder);
            this.alarms = angular.copy(this.originalAlarms);
            if (this.alarms.length > 0) {
                this.alarm = this.alarms[0];
            } else {
                this.alarms = [];
                this.alarm = undefined;
            }
        } else {
            this.alarms = [];
            this.alarm = undefined;
        }
        this.updateFormState({ index: -1, newValues: {} });
    }

    private filterGroupState(states: any[]) {
        if (this.group && states) {
            this.statesForGroup = states.filter((s) => _.some(this.group.groupStateCollection, (gs) => gs.stateGuid === s.guid));
        }
    }

    getError(alarm: Alarm) {
        return {
            level: alarm.level === AlarmLevel.Information ? 0 : alarm.level + 1,
            message: AlarmLevel[alarm.level]
        };
    }

    getColor(alarm: Alarm) {
        return this._alarmHelper.alarmLevelColor(alarm.level);
    }

    select(alarm: any, $event) {
        this.alarm = alarm;
    }

    isSelected(alarm): boolean {
        return this.alarm === alarm;
    }

    delete(guid: string) {
        if (!guid) {
            this.alarm = undefined;
            if (this.alarmIndex !== undefined) {
                _.pullAt(this.alarms, this.alarmIndex);
                this.onDelete({ index: this.alarmIndex });
                this.alarmIndex = undefined;
                this.updateAlarms(this.alarms);
            }
        } else {
            this.deleteAlarm(this.identifier.guid, guid)
                .then(() => {
                    if (this.alarm.guid === guid) {
                        this.alarm = undefined;
                    }
                    this.onDelete({ index: this.alarms.indexOf(this.alarms.find((a) => a.guid === guid)) });
                    this.alarms = this.alarms.filter((a) => a.guid !== guid);
                    this.updateAlarms(this.alarms);
                })
                .catch(() => {
                    this._loggerService.logError('Alarm could not be deleted.');
                });
        }
    }

    onIsManualChange($data: boolean, $index: number) {
        this.updateFormState({ index: $index, newValues: { isManual: $data } });
    }

    onLevelChange($data: AlarmLevel, $index: number) {
        this.updateFormState({ index: $index, newValues: { level: $data } });
    }

    onValueTypeChange($data: string, $index: number) {
        this.updateFormState({ index: $index, newValues: { value: $data } });
    }

    save() {
        if (this.form.$valid) {
            this.saveAlarm(this.identifier.guid, this.alarms)
                .then((data) => {
                    this.groupAlarms[this.identifier.guid] = data[this.identifier.guid];
                    this.refresh();
                    this._loggerService.logSuccess('Alarms were sucessfully saved.');
                    this.form.$setPristine();
                })
                .catch(() => {
                    this._loggerService.logError('Cannot save alarms.');
                });
        }
    }

    cancel() {
        this.form.$setValidity('duplicates', true, this.form);
        this.alarms = angular.copy(this.originalAlarms);
    }

    private updateAlarms(alarms: Alarm[]) {
        this.groupAlarms[this.identifier.guid] = alarms;
        this._loggerService.logSuccess('Alarm sucessfully deleted.');
        this.form.$setDirty();
        this.updateFormState({ index: -1, newValues: {} });
    }
}

export const wtIdentifierAlarms: ComponentDefinition = {
    componentId: 'wtIdentifierAlarms',
    controller: IdentifierAlarmsController,
    template: require('./identifierAlarms.component.html'),
    controllerAs: 'vm',
    bindings: {
        alarm: '=',
        alarms: '=',
        form: '=',
        formState: '<',
        group: '=',
        groupAlarms: '=',
        identifier: '<',
        onDelete: '&?',
        updateFormState: '&'
    }
};
